'use client'

import { useRef, useState, useEffect } from 'react'

import axios from '@/utils/assessmentAxiosInterceptor'
import { getCookie, getDomain } from '@/utils/utils'

import Icon from '@/components/Icon/Icon'
import Button from '@/components/Button/Button'
import Input from '@/components/Input/Input'

import styles from './Auth.module.scss'
import classNames from 'classnames/bind'
const classes = classNames.bind(styles)

export default ({ setEmail, setPassword, setSelectedType, setError }) => {
    const emailInput = useRef(null)
    const passwordInput = useRef(null)

    const [revealPassword, setRevealPassword] = useState(false)
    const [authMethod, setAuthMethod] = useState(false)

    useEffect(() => {
        localStorage.removeItem('googleAuth')
    }, [])

    const submitLogin = async () => {
        setError(null)
        grecaptcha.ready(() => {
            grecaptcha.execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, { action: 'submit' }).then(async (token) => {
                // Post the request to the server.
                try {
                    const { data } = await axios.post('/assessment/api/auth/login', {
                        email: emailInput.current.value,
                        password: passwordInput.current.value,
                        captchaToken: token,
                    })

                    if (data.verification || data.recaptchaFailure) {
                        setEmail(emailInput.current.value)
                        setPassword(passwordInput.current.value)

                        if (data.recaptchaFailure) {
                            setSelectedType('recaptcha-verify')
                        }
                        return
                    }

                    window.location = '/'
                } catch (e) {
                    if (e?.response?.data) {
                        setError(e.response.data.message)
                    }
                }
            })
        })
    }

    const handlePasswordEyeClick = () => {
        setRevealPassword(!revealPassword)
        setTimeout(() => {
            passwordInput.current.focus()
        }, 50)
    }

    useEffect(() => {
        if (authMethod) {
            const checkLoggedIn = async () => {
                if (!!getCookie('accessToken')) {
                    try {
                        const { data } = await axios.get('/assessment/api/auth/getId')
                        if (data.success) {
                            window.location.href = '/'
                            return
                        }
                    } catch (e) {}
                }

                setTimeout(() => {
                    if (authMethod) checkLoggedIn()
                }, 1000)
            }

            checkLoggedIn()
        }
    }, [authMethod])

    return (
        <>
            <form
                className={classes('form')}
                onSubmit={(e) => e.preventDefault()}
                onKeyUp={({ key }) => key == 'Enter' && submitLogin()}
            >
                <h1 className={classes('title')}>Login to your account</h1>
                <p className={classes('subtext')}>Welcome back! Please enter your details.</p>
                <div className={classes('input-container')}>
                    <Input
                        type="email"
                        placeholder="Your email"
                        required={true}
                        ref={emailInput}
                        className={classes('icon-input')}
                        autoComplete="username"
                    />
                    <div className={classes('error-icon')}>
                        <Icon name="alert-circle" width={20} height={20} />
                    </div>
                </div>
                <div className={classes('input-container')}>
                    <Input
                        type={revealPassword ? 'text' : 'password'}
                        placeholder="Password"
                        title={
                            'Password must have minimum length of 8 characters and contain at least 1 lowercase character, 1 uppercase letter, 1 number and 1 special character (!@#$%^&*).'
                        }
                        ref={passwordInput}
                        required={true}
                        className={classes('icon-input')}
                        autoComplete="current-password"
                    />
                    <div
                        className={classes('password-reveal', {
                            'password-reveal--active': revealPassword,
                        })}
                        onMouseDown={handlePasswordEyeClick}
                    >
                        {revealPassword && <Icon name="eye" width={20} height={20} />}
                        {!revealPassword && <Icon name="eye-off" width={20} height={20} />}
                    </div>
                </div>
                <a href="#" onClick={() => setSelectedType('password-reset')}>
                    Forgotten your password?
                </a>
            </form>
            <Button
                className={classes('login-button')}
                onClick={(e) => {
                    e.preventDefault()
                    localStorage.removeItem('googleAuth')
                    submitLogin()
                }}
                text="Login"
            />
            <div className={classes('or-separator')}>
                <div className={classes('line')}></div>
                <span>or</span>
                <div className={classes('line')}></div>
            </div>
            <div className={classes('buttons')}>
                <button
                    className={classes('auth-button')}
                    onClick={() => {
                        localStorage.setItem('googleAuth', true)
                        window.open(
                            /* We have to replace the dev to qa cause dev and qa uses one cognito and the url belongs to cognito not to our server */
                            `${getDomain('auth').replace(
                                'dev',
                                'qa'
                            )}/oauth2/authorize?identity_provider=Google&redirect_uri=${getDomain('api').replace(
                                'dev',
                                'qa'
                            )}/assessment/api/auth/authCode&response_type=CODE&client_id=${
                                process.env.NEXT_PUBLIC_ENVIRONMENT == 'prod'
                                    ? '5k3kkl8a93gh62lob5l4j6k50v'
                                    : '1qvgujv2lmsinmkb28d4ctqnt7'
                            }&scope=email openid phone profile`,
                            null,
                            { popup: true }
                        )
                        setAuthMethod(true)
                    }}
                >
                    <Icon name="google" />
                    <span>Sign in with Google</span>
                </button>
            </div>
            <span className={classes('login-link')}>
                Don't have an account?{' '}
                <a href="#" onClick={() => setSelectedType('register')}>
                    Register
                </a>
            </span>
        </>
    )
}
